import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, StatusChip, fmtRelativeTime, type Tone } from '@smena/ui';
import { QueryState } from '@/components/QueryState';
import {
  fetchDrafts,
  fetchEscalations,
  fetchIngestEvents,
  fetchPendingActions,
} from './api';

// Четыре среза, которые вместе отвечают на «что вообще происходило в аккаунте»:
// эскалации (что ушло человеку), черновики (что человек не отправил), подтверждения
// (что агент попросил разрешить) и инжест (откуда приехали брони).

export function ActivityTab({ accountId }: { accountId: string }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Escalations accountId={accountId} />
      <PendingActions accountId={accountId} />
      <Drafts accountId={accountId} />
      <IngestEvents accountId={accountId} />
    </div>
  );
}

function Escalations({ accountId }: { accountId: string }) {
  const q = useQuery({
    queryKey: ['escalations', accountId],
    queryFn: () => fetchEscalations(accountId),
  });
  return (
    <Section title="Эскалации" q={q} empty="Эскалаций нет">
      {q.data?.map((e) => (
        <Row key={e.id}>
          <div className="min-w-0">
            <div className="truncate">{e.reason}</div>
            <div className="truncate text-[11px] text-muted">{e.text}</div>
            <div className="text-[11px] text-muted">
              {fmtRelativeTime(e.ts)}
              {e.sid ? ` · ${e.sid}` : ''}
              {e.handledByEmail ? ` · закрыл ${e.handledByEmail}` : ''}
            </div>
          </div>
          <StatusChip tone={e.status === 'open' ? 'amber' : 'green'}>{e.status}</StatusChip>
        </Row>
      ))}
    </Section>
  );
}

function PendingActions({ accountId }: { accountId: string }) {
  const q = useQuery({
    queryKey: ['pending-actions', accountId],
    queryFn: () => fetchPendingActions(accountId),
  });
  return (
    <Section title="Подтверждения действий" q={q} empty="Запросов не было">
      {q.data?.map((p) => (
        <Row key={p.id}>
          <div className="min-w-0">
            <div className="truncate font-mono text-xs">{p.toolName}</div>
            <div className="text-[11px] text-muted">
              {fmtRelativeTime(p.createdAt)} · {p.sid}
              {p.decidedByEmail ? ` · решил ${p.decidedByEmail}` : ''}
            </div>
            {p.reason ? <div className="text-[11px] text-muted">{p.reason}</div> : null}
          </div>
          <StatusChip tone={pendingTone(p.status)}>{p.status}</StatusChip>
        </Row>
      ))}
    </Section>
  );
}

function Drafts({ accountId }: { accountId: string }) {
  const q = useQuery({ queryKey: ['drafts', accountId], queryFn: () => fetchDrafts(accountId) });
  return (
    <Section title="Черновики ответов" q={q} empty="Черновиков нет">
      {q.data?.map((d) => (
        <Row key={d.id}>
          <div className="min-w-0">
            <div className="truncate">{d.replyText}</div>
            <div className="text-[11px] text-muted">
              {d.intent} · {fmtRelativeTime(d.createdAt)}
              {d.sid ? ` · ${d.sid}` : ''}
            </div>
          </div>
          <StatusChip tone={d.handledAt ? 'green' : 'amber'}>
            {d.handledAt ? 'обработан' : 'ждёт'}
          </StatusChip>
        </Row>
      ))}
    </Section>
  );
}

function IngestEvents({ accountId }: { accountId: string }) {
  const q = useQuery({
    queryKey: ['ingest-events', accountId],
    queryFn: () => fetchIngestEvents(accountId),
  });
  return (
    <Section title="Инжест броней" q={q} empty="Событий инжеста нет">
      {q.data?.map((e) => (
        <Row key={e.id}>
          <div className="min-w-0">
            <div className="truncate">
              {e.source}
              {e.propertyTitle ? ` · ${e.propertyTitle}` : ''}
              {e.bookingGuest ? ` · ${e.bookingGuest}` : ''}
            </div>
            <div className="text-[11px] text-muted">{fmtRelativeTime(e.ts)}</div>
          </div>
          <StatusChip tone={e.action === 'error' ? 'red' : e.action === 'skipped' ? 'muted' : 'green'}>
            {e.action}
          </StatusChip>
        </Row>
      ))}
    </Section>
  );
}

function Section({
  title,
  q,
  empty,
  children,
}: {
  title: string;
  q: { isLoading: boolean; error: unknown; data?: unknown[] };
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="max-h-[60vh] overflow-hidden">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="max-h-[48vh] overflow-auto">
        <QueryState
          isLoading={q.isLoading}
          error={q.error}
          isEmpty={q.data?.length === 0}
          emptyText={empty}
        >
          <div className="flex flex-col gap-2 text-sm">{children}</div>
        </QueryState>
      </CardContent>
    </Card>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line/60 pb-2 last:border-0">
      {children}
    </div>
  );
}

function pendingTone(status: string): Tone {
  if (status === 'executed') return 'green';
  if (status === 'rejected' || status === 'expired') return 'red';
  return 'amber';
}
